import { type Editor, useEditorState } from '@tiptap/react';
import { type FC, type ReactElement, type SVGProps, useState } from 'react';

import AlignJustifyIcon from '@/assets/svg/ui-elements/align-justify.svg';
import AlignLeftIcon from '@/assets/svg/ui-elements/align-left.svg';
import AlignRightIcon from '@/assets/svg/ui-elements/align-right.svg';
import BoldIcon from '@/assets/svg/ui-elements/bold.svg';
import CodeIcon from '@/assets/svg/ui-elements/code.svg';
import ItalicIcon from '@/assets/svg/ui-elements/italic.svg';
import LetterTextIcon from '@/assets/svg/ui-elements/letter-text.svg';
import LinkIcon from '@/assets/svg/ui-elements/link-2.svg';
import StrikethroughIcon from '@/assets/svg/ui-elements/strikethrough.svg';
import UnderlineIcon from '@/assets/svg/ui-elements/underline.svg';

import { IconButton } from '../Button';
import Select from '../Select';
import Separator from '../Separator';
import { LinkDialog } from './link-dialog';
import { StyledStylePicker, StyledToolbar, StyledToolbarRow } from './RichTextEditor.styled';

type ParagraphStyle = 'paragraph' | 'h1' | 'h2' | 'h3';

const PARAGRAPH_OPTIONS = [
  { value: 'paragraph', label: 'Paragraph' },
  { value: 'h1', label: 'Heading 1' },
  { value: 'h2', label: 'Heading 2' },
  { value: 'h3', label: 'Heading 3' },
];

type ToolbarToggleProps = {
  label: string;
  icon: ReactElement<SVGProps<SVGSVGElement>>;
  active: boolean;
  disabled: boolean;
  onClick: () => void;
};

const ToolbarToggle: FC<ToolbarToggleProps> = ({ label, icon, active, disabled, onClick }) => (
  <IconButton
    type="button"
    color="tertiary"
    variant="outline"
    size="xs"
    aria-label={label}
    selected={active}
    disabled={disabled}
    onClick={onClick}
  >
    {icon}
  </IconButton>
);

export const Toolbar: FC<{ editor: Editor; disabled: boolean }> = ({ editor, disabled }) => {
  const [showStyleRow, setShowStyleRow] = useState(false);
  const [linkOpen, setLinkOpen] = useState(false);

  const state = useEditorState({
    editor,
    selector: ({ editor: current }) => ({
      bold: current.isActive('bold'),
      italic: current.isActive('italic'),
      underline: current.isActive('underline'),
      strike: current.isActive('strike'),
      code: current.isActive('code'),
      link: current.isActive('link'),
      alignLeft: current.isActive({ textAlign: 'left' }),
      alignJustify: current.isActive({ textAlign: 'justify' }),
      alignRight: current.isActive({ textAlign: 'right' }),
      paragraphStyle: ((['h1', 'h2', 'h3'] as const).find((heading) =>
        current.isActive('heading', { level: Number(heading[1]) }),
      ) ?? 'paragraph') as ParagraphStyle,
      href: (current.getAttributes('link').href as string | undefined) ?? '',
    }),
  });

  const setParagraphStyle = (value: ParagraphStyle) => {
    const chain = editor.chain().focus();
    if (value === 'paragraph') chain.setParagraph().run();
    else chain.toggleHeading({ level: Number(value[1]) as 1 | 2 | 3 }).run();
  };

  return (
    <StyledToolbar>
      {showStyleRow ? (
        <StyledStylePicker>
          <Select
            size="sm"
            ariaLabel="Paragraph style"
            options={PARAGRAPH_OPTIONS}
            value={state.paragraphStyle}
            disabled={disabled}
            onChange={(value) => setParagraphStyle(value as ParagraphStyle)}
          />
        </StyledStylePicker>
      ) : null}

      <StyledToolbarRow>
        <ToolbarToggle
          label="Text styles"
          icon={<LetterTextIcon />}
          active={showStyleRow}
          disabled={disabled}
          onClick={() => setShowStyleRow((shown) => !shown)}
        />

        <Separator orientation="vertical" tone="border" />

        <ToolbarToggle
          label="Bold"
          icon={<BoldIcon />}
          active={state.bold}
          disabled={disabled}
          onClick={() => editor.chain().focus().toggleBold().run()}
        />
        <ToolbarToggle
          label="Italic"
          icon={<ItalicIcon />}
          active={state.italic}
          disabled={disabled}
          onClick={() => editor.chain().focus().toggleItalic().run()}
        />
        <ToolbarToggle
          label="Underline"
          icon={<UnderlineIcon />}
          active={state.underline}
          disabled={disabled}
          onClick={() => editor.chain().focus().toggleUnderline().run()}
        />
        <ToolbarToggle
          label="Strikethrough"
          icon={<StrikethroughIcon />}
          active={state.strike}
          disabled={disabled}
          onClick={() => editor.chain().focus().toggleStrike().run()}
        />
        <ToolbarToggle
          label="Inline code"
          icon={<CodeIcon />}
          active={state.code}
          disabled={disabled}
          onClick={() => editor.chain().focus().toggleCode().run()}
        />

        <Separator orientation="vertical" tone="border" />

        <ToolbarToggle
          label="Align left"
          icon={<AlignLeftIcon />}
          active={state.alignLeft}
          disabled={disabled}
          onClick={() => editor.chain().focus().setTextAlign('left').run()}
        />
        <ToolbarToggle
          label="Justify"
          icon={<AlignJustifyIcon />}
          active={state.alignJustify}
          disabled={disabled}
          onClick={() => editor.chain().focus().setTextAlign('justify').run()}
        />
        <ToolbarToggle
          label="Align right"
          icon={<AlignRightIcon />}
          active={state.alignRight}
          disabled={disabled}
          onClick={() => editor.chain().focus().setTextAlign('right').run()}
        />

        <Separator orientation="vertical" tone="border" />

        <ToolbarToggle
          label="Link"
          icon={<LinkIcon />}
          active={state.link}
          disabled={disabled}
          onClick={() => setLinkOpen(true)}
        />
      </StyledToolbarRow>

      <LinkDialog
        open={linkOpen}
        initialHref={state.href}
        hasLink={state.link}
        onClose={() => setLinkOpen(false)}
        onApply={(href) => {
          editor.chain().focus().extendMarkRange('link').setLink({ href }).run();
          setLinkOpen(false);
        }}
        onRemove={() => {
          editor.chain().focus().extendMarkRange('link').unsetLink().run();
          setLinkOpen(false);
        }}
      />
    </StyledToolbar>
  );
};
